/**
 * Semantic design tokens (TMMAPP-0001, brand roles TMMAPP-0035). Every colour the app paints
 * comes from the active brand theme's resolved roles for the current mode — the tenant's theme
 * from saas-auth, or the built-in `PLATFORM_DEFAULT_THEME` of the same shape. Components read them
 * through `resolveTokens(theme)` / `theme.tokens`, never as literals (the eslint no-raw-color
 * guard enforces it outside src/theme/**). `Tokens` uses the brand role names verbatim — incl.
 * `brandBoundary` (SASATH-0352), the served brand for non-text boundaries on a canvas — plus the
 * categorical data colours, the three elevations, the radius scale and the derived `textLabel`
 * (TMMAPP-0099). `contrastAudit.test.ts` measures every actual-use pair and
 * `brandGuarantee.test.ts` proves each served pair is one saas-auth guarantees.
 */
import type {
  BrandMode,
  BrandRoles,
  BrandTheme,
  CategoricalColor,
  Elevation,
  Radius,
} from './brandTheme';
import { textLabelFor, type LabelCanvas } from './textLabel';

export type ThemeMode = BrandMode;

/** Fully rounded ends (chips) — a shape, not a brand radius. */
export const PILL_RADIUS = 999;

export type AppTokens = BrandRoles & {
  /** Eight data colours in fixed order: `fill` / `ink` / `soft` / `line` (chart stroke). */
  categorical: CategoricalColor[];
  /** Box-shadows for cards, raised surfaces (menus, snackbars) and dialogs. */
  elevation: Elevation;
  /** Corner radii in px; `control` is also MUI's `shape.borderRadius` base. */
  radius: Radius;
  /**
   * The prototype's small-label text colour on `canvas` (`textLabel.ts`): `text3` where it reaches
   * 4.5:1 on that canvas, else `text2`.
   */
  textLabel: (canvas: LabelCanvas) => string;
};

export function tokensFromBrand(brand: BrandTheme, mode: ThemeMode): AppTokens {
  const roles = brand.modes[mode];
  return {
    ...roles,
    categorical: brand.categorical[mode],
    elevation: brand.elevation[mode],
    radius: brand.radius,
    textLabel: textLabelFor(roles),
  };
}

/**
 * The Home mood faces' illustration colours (TMMAPP-0154 §A 5, correcting TMMAPP-0150 item 6; P:834):
 * the prototype's saturated fills with its dark-brown features, and designed dark-mode equivalents
 * of the same hues — a step deeper and less bright, so they don't glare on the dark `surface`, with
 * a deeper brown for the features. They are product illustration, not tenant brand roles: no text
 * sits on a face (its name is beside it), and the features clear 6:1 on every fill in both modes
 * (`categorical.test.ts`).
 */
export const MOOD_FACE_COLOURS = {
  light: {
    stressed: '#f2a59a',
    overwhelmed: '#f5b98a',
    okay: '#f6d68a',
    good: '#b9d6a6',
    confident: '#8fc4a0',
    features: '#3b2a1e',
  },
  dark: {
    stressed: '#d98c81',
    overwhelmed: '#dba174',
    okay: '#d9bb72',
    good: '#9fbd8c',
    confident: '#77ab88',
    features: '#2a1d14',
  },
} as const;
